import { StrictMode } from 'react';

import { ThemeProvider } from '@/providers/theme-provider';
import { TooltipProvider } from '@/ux-common/components/tooltip/tooltip';

import { AuthProvider } from './auth/auth-provider';

interface AppProvidersProps {
  children?: React.ReactNode;
}

/**
 * AppProviders adds a stack of cross-cutting concerns to the React context:
 * - Authentication provider
 * - Theme provider
 * - Tooltip provider
 * - Toaster notifications provider
 *
 * One tooltip provider serves both tooltip systems: the vendored registry
 * Tooltip and the one bundled in @crossnokaye/ux-common resolve to the same
 * @radix-ui/react-tooltip instance, so they share a context. If that ever stops
 * being true, Radix throws on the first hover — add the package's own provider
 * back above this one.
 *
 * delayDuration is 0 by decision, not by default — UX-3107's call, made 2026-10-07:
 * tooltips show instantly everywhere, and dense row grids opt into a dwell instead.
 * The one deliberate exception is tables, which mount TableTooltipIsland
 * (rationale on TABLE_TOOLTIP_DELAY_MS) so sweeping the mouse across rows does
 * not fire a tooltip per cell. Keep the value explicit: it is app-wide policy,
 * and an unset provider reads as an accident.
 *
 * LaunchDarkly is initialized in main.tsx during app bootstrap
 */
export const AppProviders = ({ children }: AppProvidersProps) => {
  return (
    <StrictMode>
      <AuthProvider>
        <ThemeProvider>
          <TooltipProvider delayDuration={0}>{children}</TooltipProvider>
        </ThemeProvider>
      </AuthProvider>
    </StrictMode>
  );
};
